import {
  AIWrapper,
  Box,
  Button,
  CancelIconButton,
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatMinutes,
  GeneralValidationErrors,
  Grid,
  GridBreak,
  IconButton,
  pn,
  Stack,
  Tooltip,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { InfoIcon } from "components/common/InfoIcon";
import { DatePicker, DurationInput, FilePanel, Input, Select, SubmitButton } from "components";
import { CopyLinkButton } from "components/common/CopyLinkButton";
import {
  AiProcessedAttendee,
  LkStudentServiceDisplayFieldId,
  Permission,
  StudentServiceAddAttendeeRequest,
  StudentServiceAddAttendeeResponse,
  StudentServiceTemplate,
  StudentServiceTemplateAdditionalEntry,
  StudentServiceTemplateAttendee,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
  UploadFile,
} from "generated/openApi";
import { useRouter } from "hooks";
import produce from "immer";
import "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { studentLinkPageRoute } from "../StudentLinkPage/StudentLinkPage";
import { AdditionalEntries } from "./AdditionalEntries/AdditionalEntries";
import { QrCodes } from "./QrCodes";
import { initDataModel } from "utils";
import moment from "moment";

// export type AdditionalServiceType = {
//   displayOrder: number;
//   durationInMinutes?: number | null;
//   studentServiceTypeId: number;
//   studentServiceSubTypeId?: number | null;
// };

export const StudentServiceTemplatePageBody = (props: {
  rootPermission: Permission;
  crudMode: CrudMode;
  template: StudentServiceTemplate;
  finishedFetch: boolean;
  setTemplate: (value: StudentServiceTemplate) => void;
  addAttendee: (request: StudentServiceAddAttendeeRequest) => void;
  attendanceTabs: StudentServiceTemplateAttendee[] | undefined;
  viewResource: StudentServiceTemplateViewResource;
  editResource: StudentServiceTemplateEditResource;
  studentServiceAddResponse: StudentServiceAddAttendeeResponse;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const selectedServiceType = props.viewResource.serviceTypes?.find((x) => x.id === props.template.serviceTypeId);
  const serviceSubTypes = props.editResource.serviceSubTypes?.filter(
    (x) => x.serviceTypeId === props.template.serviceTypeId
  );
  const generationValidationErrors = props.validationErrors[pn<StudentServiceTemplate>("generalValidationErrors")];
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [currentTab, setCurrentTab] = useState<StudentServiceTemplateAttendee>();

  // AI Start
  const [aiAttendees, setAiAttendees] = useState<AiProcessedAttendee[]>();
  const [futureDateWarning, setFutureDateWarning] = useState(false);
  const [aiDate, setAiDate] = useState<Date>(new Date());
  const [aiBulkCheckIn, setAiBulkCheckIn] = useState("");
  const [aiBulkCheckOut, setAiBulkCheckOut] = useState("");
  const [manualAiAttendeeIndexes, setManualAiAttendeeIndexes] = useState<number[]>([]);
  const [aiAttendeeLookupStatuses, setAiAttendeeLookupStatuses] = useState<
    Record<number, "checking" | "matched" | "notFound">
  >({});
  const aiLookupTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const aiLookupRequestIds = useRef<Record<number, number>>({});
  // AI End


  const [additionalTypes, setAdditionalTypes] = useState<StudentServiceTemplateAdditionalEntry[]>([]);
  const [linkCopy, setLinkCopy] = useState<boolean>(false);
  const [splitDuration, setSplitDuration] = useState(false);
  const [uploadFile, setUploadFile] = useState<UploadFile>();

  const [debounce1, setDebounce1] = useState(false);
  const [debounce2, setDebounce2] = useState(false);

  useEffect(() => {
    return () => {
      Object.values(aiLookupTimers.current).forEach(clearTimeout);
    };
  }, []);

  const displayFields = props.viewResource.displayFields || [];

  const languageDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Language);
  const modalityDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Modality);
  const splitDurationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.SplitDuration);
  const serviceLocationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.ServiceLocation);
  const isServiceDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.IsService);
  const referralDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Referral);
  const topicsDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Topics);
  const subTypeDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.SubType);

  useEffect(() => {
    updateSplitDurations();
  }, [splitDuration]);

  useEffect(() => {
    if (props.attendanceTabs && props.attendanceTabs.length > 0) {
      setCurrentTab(props.attendanceTabs[0]);
    }
  }, [props.attendanceTabs]);


  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const debounceUseEffect = async () => {
      if (props.template.additionalStudentServiceTypes && props.template.additionalStudentServiceTypes != '[]') {
        setAdditionalTypes(JSON.parse(props.template.additionalStudentServiceTypes));
        await sleep(1200);
        setDebounce1(false);
      }
      else {
        await sleep(1200);
        setDebounce1(false);
      }
    }

    if (!debounce1 && props.finishedFetch) {
      setDebounce1(true);
      debounceUseEffect();
    }
  }, [props.template]);


  useEffect(() => {
    const debounceUseEffect2 = async () => {
      props.setTemplate(
        produce(props.template, (draft) => {
          draft.additionalStudentServiceTypes = JSON.stringify(additionalTypes);
        })
      );

      await sleep(1200);
      setDebounce2(false);
    }

    if (!debounce2 && props.finishedFetch) {
      setDebounce2(true);
      debounceUseEffect2();
    }
  }, [additionalTypes]);

  const splitDurationField = () => {
    return (
      <Checkbox label="Split Duration" checked={splitDuration} onChange={(e) => setSplitDuration(!splitDuration)} />
    );
  };

  const updateSplitDurations = () => {
    if (splitDuration && props.template.durationInMinutes != null) {
      if (additionalTypes != undefined && additionalTypes.length > 0) {
        let splitAmount = (props.template.durationInMinutes / (additionalTypes.length + 1));
        splitAmount = Math.ceil(Math.round(splitAmount * 100) / 100);

        setAdditionalTypes(
          produce(additionalTypes, (draft) => {
            draft.forEach((entry) => {
              entry.durationInMinutes = splitAmount!;
            });
          })
        );

        props.setTemplate(
          produce(props.template, (draft) => {
            draft.durationInMinutes = splitAmount!;
          })
        );
      }
    }
  }

  const referralTopicsField = () => {
    const propertyName = pn<StudentServiceTemplate>("referralTopics");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={topicsDisplayField?.displayName}>
        <DataArray value={props.template.referralTopics?.filter(x => x.isReferral).map((x) => `${x.name}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        label={topicsDisplayField?.displayName}
        modelProperty={property}
        required={topicsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.template.referralTopics}
        options={props.editResource.topics?.filter(x => x.isReferral)}
        getOptionLabel={(option) => `${option.name}`}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.referralTopics = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const serviceTopicsField = () => {
    const propertyName = pn<StudentServiceTemplate>("topics");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={topicsDisplayField?.displayName}>
        <DataArray value={props.template.topics?.filter(x => x.isService).map((x) => `${x.name}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        label={topicsDisplayField?.displayName}
        modelProperty={property}
        required={topicsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.template.topics}
        options={props.editResource.topics?.filter(x => x.isService)}
        getOptionLabel={(option) => `${option.name}`}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.topics = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const topicsField = () => {
    const propertyName = pn<StudentServiceTemplate>("topics");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={topicsDisplayField?.displayName}>
        <DataArray value={props.template.topics?.map((x) => `${x.name}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        label={topicsDisplayField?.displayName}
        modelProperty={property}
        required={topicsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.template.topics}
        options={props.editResource.topics}
        getOptionLabel={(option) => `${option.name}`}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.topics = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const dateField = () => {
    const propertyName = pn<StudentServiceTemplate>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.template.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.date}
        onChange={(value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" spacing="sm" alignItems="center">
          <Typography variant="inherit">{props.template.serviceType?.name}</Typography>
          {props.template.serviceType?.isForParent && <EscalatorWarningIcon />}
        </Stack>
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.serviceTypes?.find((x) => x.id === props.template.serviceTypeId)}
        options={props.viewResource.serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentServiceTemplate>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={subTypeDisplayField?.displayName}>{props.template.serviceSubType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={subTypeDisplayField?.displayName}
        required
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.template.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentServiceTemplate>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.parentCount}</DataDisplay>
    ) : (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Input
          modelProperty={property}
          required
          errors={props.validationErrors[propertyName]}
          value={props.template.parentCount}
          onChange={(e) =>
            props.setTemplate(
              produce(props.template, (draft) => {
                draft.parentCount = parseFloat(e.target.value);
              })
            )
          }
        />
        <InfoIcon text="Number of Parents Per Student" />
      </Stack>
    );
  };

  const nameField = () => {
    const propertyName = pn<StudentServiceTemplate>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.name}</DataDisplay>
    ) : (
      <Select
        freeSolo
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        inputValue={props.template.name}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.name = value?.name;
            })
          )
        }
        onInputChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.studentServiceNames(searchString)}
      />
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<StudentServiceTemplate>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.template.durationInMinutes)}</DataDisplay>
    ) : (
      <DurationInput
        required
        errors={props.validationErrors[propertyName]}
        value={props.template.durationInMinutes}
        onBlur={() => updateSplitDurations()}
        onChange={(value) => {
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.durationInMinutes = value || undefined;
            })
          )
        }
        }
      />
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentServiceTemplate>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.template.colleges?.map((x) => x.name!)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.colleges}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentServiceTemplate>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.template.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const providersField = () => {
    const propertyName = pn<StudentServiceTemplate>("providers");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.template.providers?.map((x) => `${x.lastName}, ${x.firstName}`)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.providers}
        options={props.editResource.providers}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, values) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.providers = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const languageField = () => {
    const propertyName = pn<StudentServiceTemplate>("languageId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.language?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.languages?.find((x) => x.id === props.template.languageId)}
        options={props.editResource.languages}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.languageId = value?.id;
            })
          )
        }
      />
    );
  };

  const modalityField = () => {
    const propertyName = pn<StudentServiceTemplate>("modalityId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.template.modality?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.modalities?.find((x) => x.id === props.template.modalityId)}
        options={props.editResource.modalities}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.modalityId = value?.id;
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<StudentServiceTemplate>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.template.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.template.note}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const addAttendees = () => {
    var request: StudentServiceAddAttendeeRequest = {
      serviceDate: aiDate,
      attendees: aiAttendees,
      studentServiceTemplateId: props.template.id,
    }
    props.addAttendee(request);
  }

  const getAiTimeMinutes = (value?: string) => {
    if (!value) return null;

    const parsedTime = moment(value, ["h:mm A", "h:mm a", "h:mm", "HH:mm", "HH:mm:ss"], true);
    if (parsedTime.isValid()) {
      return parsedTime.hours() * 60 + parsedTime.minutes();
    }

    const parsedDate = moment(value);
    if (parsedDate.isValid()) {
      return parsedDate.hours() * 60 + parsedDate.minutes();
    }

    return null;
  };

  const getAiDurationInMinutes = (checkIn?: string, checkOut?: string) => {
    const checkInMinutes = getAiTimeMinutes(checkIn);
    const checkOutMinutes = getAiTimeMinutes(checkOut);

    if (checkInMinutes == null || checkOutMinutes == null) return "";

    const minutesInDay = 24 * 60;
    const durationInMinutes =
      checkOutMinutes >= checkInMinutes ? checkOutMinutes - checkInMinutes : checkOutMinutes + minutesInDay - checkInMinutes;

    return durationInMinutes.toString();
  };

  const updateAiAttendee = (index: number, field: "customId1" | "studentName" | "in" | "out", value: string) => {
    if (!aiAttendees) return;

    if (field === "customId1") {
      const attendeeIndex = aiAttendees[index].index;
      const requestId = (aiLookupRequestIds.current[attendeeIndex] || 0) + 1;
      aiLookupRequestIds.current[attendeeIndex] = requestId;
      clearTimeout(aiLookupTimers.current[attendeeIndex]);

      setAiAttendees(
        produce(aiAttendees, (draft) => {
          draft[index].customId1 = value;
          draft[index].studentId = undefined;
          draft[index].studentName = "";
        })
      );

      if (!value.trim()) {
        setAiAttendeeLookupStatuses((current) => {
          const next = { ...current };
          delete next[attendeeIndex];
          return next;
        });
        return;
      }

      aiLookupTimers.current[attendeeIndex] = setTimeout(async () => {
        setAiAttendeeLookupStatuses((current) => ({ ...current, [attendeeIndex]: "checking" }));
        try {
          const response = await ApiService.instance.get("/Students/GetStudentByCustomId", {
            params: { customId: value.trim(), customIdType: 1 },
            validateStatus: (status) => status === 404 || (status >= 200 && status < 300),
          });
          if (aiLookupRequestIds.current[attendeeIndex] !== requestId) return;

          if (response.status === 404) {
            setAiAttendeeLookupStatuses((current) => ({ ...current, [attendeeIndex]: "notFound" }));
            return;
          }

          const student = response.data;

          setAiAttendees((current) =>
            produce(current || [], (draft) => {
              const attendee = draft.find((item) => item.index === attendeeIndex);
              if (attendee) {
                attendee.studentId = student.id;
                attendee.studentName = `${student.lastName}, ${student.firstName}`;
              }
            })
          );
          setAiAttendeeLookupStatuses((current) => ({ ...current, [attendeeIndex]: "matched" }));
        } catch {
          if (aiLookupRequestIds.current[attendeeIndex] !== requestId) return;

          setAiAttendeeLookupStatuses((current) => {
            const next = { ...current };
            delete next[attendeeIndex];
            return next;
          });
        }
      }, 750);
      return;
    }

    setAiAttendees(
      produce(aiAttendees, (draft) => {
        const attendee = draft[index];
        attendee[field] = value;

        if (field === "in" || field === "out") {
          attendee.duration = getAiDurationInMinutes(attendee.in, attendee.out);
        }
      })
    );
  };

  const updateAllAiAttendees = (field: "in" | "out", value: string) => {
    if (!aiAttendees) return;

    setAiAttendees(
      produce(aiAttendees, (draft) => {
        draft.forEach((attendee) => {
          attendee[field] = value;
          attendee.duration = getAiDurationInMinutes(attendee.in, attendee.out);
        });
      })
    );
  };

  const addAiAttendee = () => {
    const nextIndex = (aiAttendees || []).reduce((maxIndex, attendee) => Math.max(maxIndex, attendee.index), -1) + 1;

    setAiAttendees(
      produce(aiAttendees || [], (draft) => {
        draft.push({
          customId1: "",
          duration: "",
          in: "",
          index: nextIndex,
          out: "",
          studentName: "",
        });
      })
    );
    setManualAiAttendeeIndexes((current) => [...current, nextIndex]);
  };

  const removeAiAttendee = (index: number) => {
    if (!aiAttendees) return;
    const removedIndex = aiAttendees[index]?.index;
    aiLookupRequestIds.current[removedIndex] = (aiLookupRequestIds.current[removedIndex] || 0) + 1;
    clearTimeout(aiLookupTimers.current[removedIndex]);
    setAiAttendeeLookupStatuses((current) => {
      const next = { ...current };
      delete next[removedIndex];
      return next;
    });

    setAiAttendees(
      produce(aiAttendees, (draft) => {
        draft.splice(index, 1);
      })
    );
    setManualAiAttendeeIndexes((current) => current.filter((manualIndex) => manualIndex !== removedIndex));
  };

  const getAiAttendeeError = (index: number) => {
    return props.studentServiceAddResponse?.erroredAttendees?.find((e) => index in e)?.[index];
  };

  const getAiAttendeeSuccess = (index: number) => {
    return props.studentServiceAddResponse?.addedAttendees?.some((e) => e === index);
  };

  const renderAttendance = () => {
    return (
      <Grid container spacing="sm">
        <Grid item xs={2}>
          <Select
            label="Check-In Date"
            value={currentTab}
            options={props.attendanceTabs}
            getOptionLabel={(option) => option.label || ""}
            onChange={(e, value) =>
              setCurrentTab(value)
            }
          />
        </Grid>

        <GridBreak />

        <Grid item xs={12}>
          {currentTab && (
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Grid container spacing="lg">
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">
                      {props.viewResource.customIdTypes && props.viewResource.customIdTypes.length > 0
                        ? props.viewResource.customIdTypes![0].name
                        : "Student ID"}
                    </Typography>
                  </Grid>
                  {props.template.enableAdditionalServiceSelect && (
                    <Grid item xs={4}>
                      <Typography variant="body1">Services Attended</Typography>
                    </Grid>
                  )}
                  <Grid item xs={2}>
                    <Typography variant="body1">Student</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Check-In</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Check-Out</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1">Duration (min.)</Typography>
                  </Grid>
                  <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                    <Typography variant="body1">Unknown Student</Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentTab.studentServices?.map((student, index) => (
                <Grid item xs={12}>
                  <Grid container spacing="lg">
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.customId}</Typography>
                    </Grid>
                    {props.template.enableAdditionalServiceSelect && (
                      <Grid item xs={4}>
                        <Typography variant="body1">{student.servicesAttended}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={2}>
                      <Typography variant="body1">{student.studentName}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.startTime || "---"}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      <Typography variant="body1">{student.endTime || "---"}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body1">{student.durationInMinutes || "---"}</Typography>
                    </Grid>
                    <Grid item xs={props.template.enableAdditionalServiceSelect ? 1 : 2}>
                      {student.isPartial && student.partialStudentId && (
                        <IconButton
                          onClick={() =>
                            navigate({
                              pathname: studentLinkPageRoute.getPath({
                                studentPartialId: student.partialStudentId!,
                                screenType: "dialog",
                              }),
                            })
                          }
                        >
                          <PriorityHighIcon sx={{ color: "red" }} />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderAiAttendance = () => {
    const customIdLabel =
      props.viewResource.customIdTypes && props.viewResource.customIdTypes.length > 0
        ? props.viewResource.customIdTypes![0].name
        : "Student ID";

    return (
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.action.hover,
            padding: 2,
          }}
        >
          <Grid container spacing="sm" alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing="xs">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="h6">Parsed Attendees</Typography>
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={!aiAttendees?.length}
                    onClick={() => {
                      Object.keys(aiLookupTimers.current).forEach((key) => {
                        const attendeeIndex = Number(key);
                        aiLookupRequestIds.current[attendeeIndex] = (aiLookupRequestIds.current[attendeeIndex] || 0) + 1;
                        clearTimeout(aiLookupTimers.current[attendeeIndex]);
                      });
                      setAiAttendees([]);
                      setManualAiAttendeeIndexes([]);
                      setAiAttendeeLookupStatuses({});
                    }}
                  >
                    Clear
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {aiAttendees?.length} attendees parsed from the uploaded file.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack>
                <DatePicker
                  label={"Check in Date"}
                  value={aiDate}
                  onChange={(value) => {
                    setAiDate(value);

                    const isValidDate = moment(value).isValid();
                    const isFutureDate = moment(value).isAfter(moment());
                    setFutureDateWarning(isValidDate && isFutureDate);
                  }}
                />
                {futureDateWarning && (
                  <Typography color={(theme) => theme.palette.warning.main} variant="caption">
                    Date is in the future.
                  </Typography>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  marginTop: 1.5,
                  paddingTop: 1.5,
                }}
              >
                <Grid container spacing="sm" alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Typography variant="body2" fontWeight={700} color="text.secondary">
                      Bulk Update
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3} />
                  <Grid item xs={6} md={2}>
                    <Stack direction="row" spacing="xs" alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Input
                          label="Check-In"
                          value={aiBulkCheckIn}
                          onChange={(e) => setAiBulkCheckIn(e.target.value)}
                        />
                      </Box>
                      <Tooltip title="Apply Check-In to rows below">
                        <span>
                          <IconButton
                            color="primary"
                            disabled={!aiBulkCheckIn}
                            onClick={() => updateAllAiAttendees("in", aiBulkCheckIn)}
                            size="small"
                            sx={{
                              border: (theme) => `1px solid ${theme.palette.primary.light}`,
                              backgroundColor: (theme) => theme.palette.background.paper,
                              flexShrink: 0,
                              height: 38,
                              width: 38,
                            }}
                          >
                            <KeyboardDoubleArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Stack direction="row" spacing="xs" alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Input
                          label="Check-Out"
                          value={aiBulkCheckOut}
                          onChange={(e) => setAiBulkCheckOut(e.target.value)}
                        />
                      </Box>
                      <Tooltip title="Apply Check-Out to rows below">
                        <span>
                          <IconButton
                            color="primary"
                            disabled={!aiBulkCheckOut}
                            onClick={() => updateAllAiAttendees("out", aiBulkCheckOut)}
                            size="small"
                            sx={{
                              border: (theme) => `1px solid ${theme.palette.primary.light}`,
                              backgroundColor: (theme) => theme.palette.background.paper,
                              flexShrink: 0,
                              height: 38,
                              width: 38,
                            }}
                          >
                            <KeyboardDoubleArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Grid item xs={8} md={2} />
                  <Grid item xs={4} md={1} />
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ padding: 2 }}>
          {aiAttendees && (
            <Grid container spacing="sm">
              {aiAttendees.map((student, index) => {
                const error = getAiAttendeeError(student.index);
                const success = getAiAttendeeSuccess(student.index);
                const hasStudentEntry = Boolean(student.customId1 || student.studentName);
                const isManualAttendee = manualAiAttendeeIndexes.includes(student.index);

                return (
                  <Grid item xs={12} key={student.index}>
                    <Box
                      sx={{
                        border: (theme) => `1px solid ${error ? theme.palette.error.light : theme.palette.divider}`,
                        borderRadius: 1,
                        padding: 1.5,
                        backgroundColor: (theme) =>
                          error ? theme.palette.error.light + "14" : success ? theme.palette.success.light + "14" : "transparent",
                      }}
                    >
                      <Grid container spacing="sm" alignItems="center">
                        <Grid item xs={12} md={2}>
                          <Input
                            label={customIdLabel}
                            value={student.customId1 || ""}
                            onChange={(e) => updateAiAttendee(index, "customId1", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Input
                            label="Student"
                            value={student.studentName || ""}
                            disabled
                            errors={aiAttendeeLookupStatuses[student.index] === "notFound" ? ["Unable to find student based on ID"] : undefined}
                            InputProps={{
                              endAdornment:
                                aiAttendeeLookupStatuses[student.index] === "matched" || student.studentId ? (
                                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                                ) : undefined,
                            }}
                            sx={
                              aiAttendeeLookupStatuses[student.index] === "matched" || student.studentId
                                ? {
                                    "& .MuiOutlinedInput-root fieldset": { borderColor: "success.main" },
                                    "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "success.main" },
                                  }
                                : undefined
                            }
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Input
                            label="Check-In"
                            value={student.in || ""}
                            onChange={(e) => updateAiAttendee(index, "in", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Input
                            label="Check-Out"
                            value={student.out || ""}
                            onChange={(e) => updateAiAttendee(index, "out", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={8} md={2}>
                          <Input
                            label="Duration (min.)"
                            value={getAiDurationInMinutes(student.in, student.out) || student.duration || ""}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={4} md={1}>
                          <Stack direction="row" spacing="xs" alignItems="center" justifyContent="flex-end">
                            {!student.studentId && !isManualAttendee && (
                              <IconButton>
                                <PriorityHighIcon sx={{ color: "error.main" }} />
                              </IconButton>
                            )}
                            <CancelIconButton onClick={() => removeAiAttendee(index)} size="small" />
                          </Stack>
                        </Grid>

                        {(error || success || (!student.studentId && hasStudentEntry && !isManualAttendee)) && (
                          <>
                            <GridBreak />
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color={error ? "error" : success ? "success.main" : "warning.main"}
                              >
                                {error || (success ? "Successfully added" : "No matched student.")}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addAiAttendee}>
                    Add Row
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    );
  };

  const referralField = () => {
    const propertyName = pn<StudentServiceTemplate>("referral");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.template.referral)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.template.referral}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.referral = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isServiceField = () => {
    const propertyName = pn<StudentServiceTemplate>("isService");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.template.isService)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.template.isService}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.isService = e.target.checked;
            })
          )
        }
      />
    );
  };

  const requireSignatureField = () => {
    const propertyName = pn<StudentServiceTemplate>("requireSignature");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Enables collection of digital signature." />    </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.requireSignature)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.requireSignature}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.requireSignature = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isActiveField = () => {
    const propertyName = pn<StudentServiceTemplate>("isActive");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Keeps the QR Code active." />
      </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.isActive)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.isActive}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.isActive = e.target.checked;
            })
          )
        }
      />
    );
  };

  const defaultAllField = () => {
    const propertyName = pn<StudentServiceTemplate>("enableAdditionalServiceSelect");
    const property = properties.find((x) => x.propertyName === propertyName);

    const labelWithInfo = (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {property?.displayName}
        <InfoIcon text="Allows participant to select what service they are attending." />
      </span>
    );

    return props.crudMode === "view" ? (
      <DataDisplay label={labelWithInfo}>
        {formatBoolean(props.template.enableAdditionalServiceSelect)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={labelWithInfo}
        checked={props.template.enableAdditionalServiceSelect}
        onChange={(e) =>
          props.setTemplate(
            produce(props.template, (draft) => {
              draft.enableAdditionalServiceSelect = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <>
      <Stack spacing="lg">
        <Card header="Info">
          <Grid ref={breakpointsRef} container spacing="sm">
            {generationValidationErrors && (
              <Grid item xs={12}>
                <GeneralValidationErrors errors={generationValidationErrors} />
              </Grid>
            )}

            {props.template.id != 0 && props.template.validationToken != null && (
              <Grid item xs={12}>
                <Grid direction="row" container spacing="lg">
                  <Grid item xs={11}>
                    <DataDisplay label="Public Link">{`${props.viewResource?.qrCodeUrl}`}</DataDisplay>
                  </Grid>
                  <Grid item xs={1}>
                    <CopyLinkButton
                      copyLink={`${props.viewResource?.qrCodeUrl}`}
                      showCheck={linkCopy}
                      setShowCheck={setLinkCopy}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {dateField()}
            </Grid>

            <GridBreak />

            <Grid item xs={smallFieldBreakpoints}>
              {serviceTypeField()}
            </Grid>

            {serviceSubTypes && serviceSubTypes.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {serviceSubTypeField()}
              </Grid>
            )}

            {/* {selectedServiceType?.isForParent && (
              <Grid item xs={smallFieldBreakpoints}>
                {parentCountField()}
              </Grid>
            )} */}

            <Grid item xs={smallFieldBreakpoints}>
              {durationInMinutesField()}
            </Grid>

            {splitDurationDisplayField && additionalTypes != undefined && additionalTypes?.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {splitDurationField()}
              </Grid>
            )}

            {serviceLocationDisplayField && (
              <>
                {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
                  <Grid item xs={mediumFieldBreakpoints}>
                    {collegesField()}
                  </Grid>
                ) : (
                  <Grid item xs={smallFieldBreakpoints}>
                    {schoolField()}
                  </Grid>
                )}
              </>
            )}

            <GridBreak />

            {referralDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {referralField()}
              </Grid>
            )}

            {topicsDisplayField && referralDisplayField && props.template.referral && (
              <Grid item xs={mediumFieldBreakpoints}>
                {referralTopicsField()}
              </Grid>
            )}
            <GridBreak />

            {isServiceDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {isServiceField()}
              </Grid>
            )}

            {/* Show if "Is Service" is not enabled OR if "Is Service" is enabled AND "Is Service" is checked */}
            {topicsDisplayField && ((isServiceDisplayField && props.template.isService)) && (
              <Grid item xs={mediumFieldBreakpoints}>
                {serviceTopicsField()}
              </Grid>
            )}

            {/* Show if "Is Service" is not enabled */}
            {(topicsDisplayField && !isServiceDisplayField) && (
              <Grid item xs={mediumFieldBreakpoints}>
                {topicsField()}
              </Grid>
            )}

            <GridBreak />

            <Grid item xs={12}>
              <AdditionalEntries
                template={props.template}
                crudMode={props.crudMode}
                rootPermission={props.rootPermission}
                entries={additionalTypes || []}
                setEntries={(values) => {
                  setAdditionalTypes(values);
                }}
                setEditMode={() => { }}
                viewResource={props.viewResource}
                editResource={props.editResource}
                validationErrors={props.validationErrors[pn<StudentServiceTemplate>("additionalStudentServiceTypes")] || {}}
              />
            </Grid>

            <GridBreak />

            <Grid item xs={smallFieldBreakpoints}>
              {nameField()}
            </Grid>

            <Grid item xs={largeFieldBreakpoints}>
              {providersField()}
            </Grid>

            {languageDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {languageField()}
              </Grid>
            )}

            {modalityDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {modalityField()}
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {requireSignatureField()}
            </Grid>

            <Grid item xs={smallFieldBreakpoints}>
              {isActiveField()}
            </Grid>

            {additionalTypes && additionalTypes.length > 0 && (
              <Grid item xs={smallFieldBreakpoints}>
                {defaultAllField()}
              </Grid>
            )}

            <Grid item xs={fullFieldBreakpoints}>
              {noteField()}
            </Grid>

            <GridBreak />
          </Grid>
        </Card>

        {props.template && props.template.id > 0 && (
          <QrCodes template={props.template} setTemplate={props.setTemplate} />
        )}

        {props.viewResource.canAccessAiFeature && (
          <Grid item xs={12}>
            <Card header="Upload Attendees">
              <Grid container spacing="sm">
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: (theme) => theme.palette.action.hover,
                      padding: 2,
                    }}
                  >
                    <AIWrapper validationErrors={props.validationErrors}>
                      <FilePanel
                        fileName={uploadFile?.fileName}
                        fileSize={uploadFile?.fileSize}
                        fileRef={uploadFile?.fileRef}
                        disabled={uploadFile && uploadFile?.id > 0}
                        uploadingData={uploadFile?.uploadingData}
                        imagePreviewData={uploadFile?.imagePreviewData}
                        onFileChange={(fileName, fileSize, uploadingData) => {
                          let t = initDataModel<UploadFile>({
                            fileName: fileName,
                            fileSize: fileSize,
                            uploadingData: uploadingData,
                          });
                          ApiService.openAi.uploadStudentServiceTemplateFile(t).then((response) => {
                            setAiAttendees(response);
                            setManualAiAttendeeIndexes([]);
                          });
                          setUploadFile(t);
                        }}
                      />
                    </AIWrapper>
                  </Box>
                </Grid>

                {aiAttendees && (
                  <Grid item xs={12}>
                    {renderAiAttendance()}
                  </Grid>
                )}

                {Boolean(aiAttendees?.length) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <SubmitButton
                        onClick={() => addAttendees()}
                        label={`Add ${aiAttendees?.length} attendees`}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
        )}

        <Grid item xs={smallFieldBreakpoints}>
          <Card header="Attendance">{renderAttendance()}</Card>
        </Grid>
      </Stack>

      {matchTemplatePath(studentLinkPageRoute.templatePath) && <Outlet />}
    </>
  );
};
